import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import toast from 'react-hot-toast'
import { supabase } from '../lib/supabase'
import { identifyDataFastUser } from '../lib/analytics'
import { api } from '../lib/api'
import type { AccessStatus, BillingProvider, BillingRelationship } from '../lib/api'
import { getAuthCallbackRedirectTo } from '../lib/authRedirect'
import { continuePendingNotificationRoute } from '../services/notificationRouting'
import {
  registerAppPushIfPermitted,
  revokeCurrentDevicePushToken,
} from '../services/pushNotifications'
import { setOnboardingStorageUserId } from '../utils/onboardingStorage'
import { isNativeMobile } from '../mobile/capacitor'

// User profile type from backend
export interface UserProfile {
  id: string
  email: string
  full_name: string | null
  avatar_url: string | null
  is_premium: boolean
  is_admin?: boolean
  access_status?: AccessStatus
  can_access_app?: boolean
  requires_upgrade?: boolean
  free_plan_end_date?: string | null
  legacy_free_plan?: boolean
  has_premium_access?: boolean
  has_paid_premium?: boolean
  can_use_broker_sync?: boolean
  subscription_provider?: BillingProvider | 'legacy' | null
  active_subscription_providers?: BillingProvider[]
  billing_relationships?: BillingRelationship[]
  multiple_active_billing_relationships?: boolean
  apple_subscription_active?: boolean
  google_play_subscription_active?: boolean
  stripe_subscription_active?: boolean
  billing_source_label?: string | null
  management_url?: string | null
  created_at: string
}

type AuthContextValue = {
  user: User | null
  session: Session | null
  loading: boolean
  profile: UserProfile | null
  isPremium: boolean
  refreshProfile: () => Promise<UserProfile | null>
  signUp: (params: { email: string; password: string; fullName?: string }) => Promise<{
    error?: string
    needsConfirmation?: boolean
    userId?: string
    createdAt?: string
  }>
  signIn: (params: { email: string; password: string }) => Promise<{ error?: string }>
  signOut: (scope?: 'local' | 'global') => Promise<void>
  resetPassword: (email: string) => Promise<{ error?: string }>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// Dev mode: check if auth bypass is enabled (disabled in tests)
const DEV_AUTH_BYPASS =
  import.meta.env.DEV &&
  import.meta.env.MODE !== 'test' &&
  import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
const DEV_AUTH_MODE_KEY = 'optioneer_dev_auth_mode'
type DevAuthMode = 'anonymous' | 'free' | 'premium' | 'admin'

// Mock user for dev mode (uses valid UUID for Supabase compatibility)
const DEV_USER_ID = '00000000-0000-4000-a000-000000000001'

function getInitialDevAuthMode(): DevAuthMode {
  if (!DEV_AUTH_BYPASS || typeof window === 'undefined') {
    return 'premium'
  }

  const storedMode = window.localStorage.getItem(DEV_AUTH_MODE_KEY)
  return storedMode === 'anonymous' ||
    storedMode === 'free' ||
    storedMode === 'premium' ||
    storedMode === 'admin'
    ? storedMode
    : 'premium'
}

function buildDevMockUser() {
  return {
    id: DEV_USER_ID,
    email: 'dev@optioneer.local',
    user_metadata: { full_name: 'Dev User' },
    app_metadata: { provider: 'email' },
    aud: 'authenticated',
    created_at: new Date().toISOString(),
  } as unknown as User
}

function buildDevAuthState(mode: DevAuthMode): {
  session: Session | null
  user: User | null
  profile: UserProfile | null
} {
  if (!DEV_AUTH_BYPASS || mode === 'anonymous') {
    return {
      session: null,
      user: null,
      profile: null,
    }
  }

  const user = buildDevMockUser()

  return {
    session: {
      access_token: 'dev-access-token',
      refresh_token: 'dev-refresh-token',
      expires_in: 3600,
      token_type: 'bearer',
      user,
    } as unknown as Session,
    user,
    profile: {
      id: DEV_USER_ID,
      email: 'dev@optioneer.local',
      full_name: 'Dev User',
      avatar_url: null,
      is_premium: mode === 'premium' || mode === 'admin',
      is_admin: mode === 'admin',
      access_status: mode === 'admin' ? 'admin' : mode === 'premium' ? 'premium_active' : 'no_plan',
      can_access_app: mode === 'premium' || mode === 'admin',
      requires_upgrade: mode === 'free',
      created_at: new Date().toISOString(),
    },
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [devAuthMode, setDevAuthMode] = useState<DevAuthMode>(() => getInitialDevAuthMode())
  const devAuthState = DEV_AUTH_BYPASS
    ? buildDevAuthState(devAuthMode)
    : { session: null, user: null, profile: null }

  // In dev mode with bypass, use mock user/session
  const [session, setSession] = useState<Session | null>(devAuthState.session)
  const [user, setUser] = useState<User | null>(devAuthState.user)
  const [loading, setLoading] = useState(!DEV_AUTH_BYPASS) // Skip loading in dev mode
  const [profile, setProfile] = useState<UserProfile | null>(devAuthState.profile)
  const userId = user?.id ?? null

  const refreshProfile = useCallback(async () => {
    if (!userId) {
      setProfile(null)
      return null
    }
    // Billing/account changes need a fresh result even if startup is still reading.
    const profileData = (await api.auth.getProfile({ cache: 'no-store' })) as UserProfile
    setProfile(profileData)
    return profileData
  }, [userId])

  const applyAuthSession = useCallback(
    (nextSession: Session | null, { preserveExistingUser = false } = {}) => {
      const nextUser = nextSession?.user ?? null

      // Update identity for every session transition, including logout and a
      // direct account switch. The adapter deduplicates refreshes and waits for consent.
      identifyDataFastUser(nextUser?.id ?? null)

      setSession(nextSession)
      setUser((currentUser) => {
        if (
          preserveExistingUser &&
          currentUser?.id &&
          nextUser?.id &&
          currentUser.id === nextUser.id
        ) {
          return currentUser
        }

        if (preserveExistingUser && !currentUser && !nextUser) {
          return currentUser
        }

        return nextUser
      })
    },
    []
  )

  const applyDevAuthMode = useCallback((mode: DevAuthMode) => {
    if (!DEV_AUTH_BYPASS) {
      return
    }

    const nextState = buildDevAuthState(mode)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DEV_AUTH_MODE_KEY, mode)
    }
    setDevAuthMode(mode)
    setSession(nextState.session)
    setUser(nextState.user)
    setProfile(nextState.profile)
    setLoading(false)
  }, [])

  useEffect(() => {
    if (!DEV_AUTH_BYPASS) {
      return
    }

    const handleModeChange = () => {
      applyDevAuthMode(getInitialDevAuthMode())
    }

    window.addEventListener('optioneer:dev-auth-mode-changed', handleModeChange)
    return () => window.removeEventListener('optioneer:dev-auth-mode-changed', handleModeChange)
  }, [applyDevAuthMode])

  // Fetch profile when authenticated user identity changes
  useEffect(() => {
    if (DEV_AUTH_BYPASS) return // Skip in dev mode, already have mock profile

    if (!userId) {
      setProfile(null)
      return
    }

    let cancelled = false

    // Fetch profile from backend
    api.auth
      .getProfile()
      .then((profileData) => {
        if (cancelled) return
        const nextProfile = profileData as UserProfile
        setProfile(nextProfile)

        const loginRefreshKey = `broker_sync_login_refresh_${userId}`
        if (
          nextProfile.access_status === 'premium_active' &&
          sessionStorage.getItem(loginRefreshKey) !== 'started'
        ) {
          sessionStorage.setItem(loginRefreshKey, 'started')
          void api.brokerSync
            .getStatus()
            .then((brokerStatus) => {
              if (
                brokerStatus.canRefresh &&
                (!brokerStatus.lastSuccessfulSyncAt ||
                  Date.now() - new Date(brokerStatus.lastSuccessfulSyncAt).getTime() >=
                    10 * 60 * 1000)
              ) {
                return api.brokerSync.refreshAfterLogin()
              }
              return undefined
            })
            .catch(() => {
              // Login refresh is best-effort and must never block app access.
            })
        }
      })
      .catch(() => {
        if (!cancelled) setProfile(null)
      })

    return () => {
      cancelled = true
    }
  }, [userId])

  useEffect(() => {
    if (!isNativeMobile()) return

    let cancelled = false
    let unsubscribeCustomerInfo: (() => void) | undefined
    let removeAppStateListener: (() => Promise<void>) | undefined

    void (async () => {
      const revenueCat = await import('../mobile/revenueCat')
      if (!userId) {
        await revenueCat.clearRevenueCatLocalState()
        return
      }

      await revenueCat.identifyRevenueCatUser(userId)
      if (cancelled) return

      const synchronizeCustomerInfo = async (
        _customerInfo: Awaited<ReturnType<typeof revenueCat.getRevenueCatCustomerInfo>>
      ) => {
        try {
          await revenueCat.retryNativeBackendSynchronization()
          if (!cancelled) await refreshProfile()
        } catch {
          // Webhooks and explicit paywall recovery remain authoritative fallbacks.
        }
      }

      unsubscribeCustomerInfo = revenueCat.subscribeToRevenueCatCustomerInfo((customerInfo) => {
        void synchronizeCustomerInfo(customerInfo)
      })

      void revenueCat
        .refreshRevenueCatCustomerInfo(userId)
        .then(synchronizeCustomerInfo)
        .catch(() => {
          // Webhooks and the visible paywall recovery actions remain available.
        })

      const { App } = await import('@capacitor/app')
      const listener = await App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive || cancelled) return
        void revenueCat
          .refreshRevenueCatCustomerInfo(userId)
          .then(synchronizeCustomerInfo)
          .catch(() => {
            // Foreground refresh is best-effort; the paywall provides a visible retry path.
          })
      })
      removeAppStateListener = () => listener.remove()
    })().catch((error) => {
      console.warn('[Billing] RevenueCat identity setup failed:', error)
    })

    return () => {
      cancelled = true
      unsubscribeCustomerInfo?.()
      void removeAppStateListener?.()
    }
  }, [refreshProfile, userId])

  useEffect(() => {
    if (DEV_AUTH_BYPASS || !userId || profile?.access_status !== 'premium_active') {
      return
    }

    let cancelled = false
    let requestInFlight = false
    const refreshActiveBrokerContext = async () => {
      if (cancelled || requestInFlight || document.visibilityState === 'hidden') return
      requestInFlight = true
      try {
        const brokerStatus = await api.brokerSync.getStatus()
        if (
          brokerStatus.canRefresh &&
          (!brokerStatus.lastSuccessfulSyncAt ||
            Date.now() - new Date(brokerStatus.lastSuccessfulSyncAt).getTime() >= 10 * 60 * 1000)
        ) {
          await api.brokerSync.refreshAfterLogin()
        }
      } catch {
        // Active-session refresh is best-effort and must never interrupt the app.
      } finally {
        requestInFlight = false
      }
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') {
        void refreshActiveBrokerContext()
      }
    }
    const interval = window.setInterval(() => void refreshActiveBrokerContext(), 10 * 60 * 1000)
    window.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      cancelled = true
      window.clearInterval(interval)
      window.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [profile?.access_status, userId])

  useEffect(() => {
    setOnboardingStorageUserId(userId)
  }, [userId])

  useEffect(() => {
    if (userId) {
      void continuePendingNotificationRoute()
      // Silent registration only runs after permission already exists; first-time permission
      // requests are handled by the native push prompt component.
      void registerAppPushIfPermitted()
    }
  }, [userId])

  useEffect(() => {
    // Skip real auth in dev mode
    if (DEV_AUTH_BYPASS) {
      console.warn('[Auth] DEV MODE: Auth bypass enabled, using mock user')
      return
    }

    let isMounted = true
    let authEventReceived = false
    void (async () => {
      try {
        const { data, error } = await supabase.auth.getSession()
        // An auth event is newer than this initial read, which can still be
        // waiting on native storage. Never replace that event's session.
        if (!isMounted || authEventReceived) return
        if (error) {
          console.warn('[Auth] Unable to restore the saved session. Please sign in again.')
        }
        applyAuthSession(data.session)
      } catch {
        if (isMounted && !authEventReceived) {
          // Native storage/bridge failures must release the welcome screen. Avoid
          // logging the underlying error because it can contain session details.
          console.warn('[Auth] Unable to restore the saved session. Please sign in again.')
        }
      } finally {
        if (isMounted) setLoading(false)
      }
    })()

    const { data: authListener } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!isMounted) return
      authEventReceived = true
      applyAuthSession(newSession, { preserveExistingUser: event === 'TOKEN_REFRESHED' })
      if (event === 'INITIAL_SESSION') setLoading(false)

      if (event === 'SIGNED_IN' && newSession?.user?.id) {
        void continuePendingNotificationRoute()
      }

      // Show welcome toast only on initial OAuth sign-in, not on session refresh/tab switch
      // Use sessionStorage to track if we've already shown the welcome message for this session
      if (event === 'SIGNED_IN' && newSession?.user) {
        const provider = newSession.user.app_metadata?.provider
        const sessionKey = `welcome_shown_${newSession.user.id}`
        const welcomeShown = sessionStorage.getItem(sessionKey)

        // Only show welcome toast if:
        // 1. User signed in with Google
        // 2. We haven't shown the welcome toast yet for this session
        if (provider === 'google' && !welcomeShown) {
          toast.success('Welcome! You are now signed in with Google.')
          sessionStorage.setItem(sessionKey, 'true')
        }
      }

      // Clear welcome flag when user signs out
      if (event === 'SIGNED_OUT') {
        // Clear all welcome flags from sessionStorage
        Object.keys(sessionStorage).forEach((key) => {
          if (key.startsWith('welcome_shown_')) {
            sessionStorage.removeItem(key)
          }
        })
      }
    })

    return () => {
      isMounted = false
      authListener.subscription.unsubscribe()
    }
  }, [applyAuthSession])

  const signUp = useCallback(
    async ({
      email,
      password,
      fullName,
    }: {
      email: string
      password: string
      fullName?: string
    }) => {
      if (DEV_AUTH_BYPASS) {
        applyDevAuthMode('free')
        return {}
      }

      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            // Redirect to callback page after email confirmation
            emailRedirectTo: getAuthCallbackRedirectTo(),
            // Store full name in user metadata (will be copied to profiles table)
            data: fullName ? { full_name: fullName } : undefined,
          },
        })

        // Log for debugging email issues
        if (import.meta.env.DEV || !data.session) {
          console.warn('[Auth] signUp response:', {
            hasUser: !!data.user,
            hasSession: !!data.session,
            userId: data.user?.id,
            userEmail: data.user?.email,
            emailConfirmedAt: data.user?.email_confirmed_at,
            confirmationSentAt: data.user?.confirmation_sent_at,
            identities: data.user?.identities?.length || 0,
            error: error?.message,
          })
        }

        if (error) {
          return { error: error.message }
        }

        // Check if user already exists (Supabase returns user with empty identities)
        // This happens when someone tries to sign up with an existing email
        if (data.user && data.user.identities && data.user.identities.length === 0) {
          return {
            error:
              'An account with this email already exists. Please sign in instead, or use a different email.',
          }
        }

        // Check if confirmation email should have been sent
        // If no session and user exists, email confirmation is required
        const needsConfirmation = !data.session && !!data.user

        // If user was created but no confirmation_sent_at, email might have failed
        if (needsConfirmation && data.user && !data.user.confirmation_sent_at) {
          console.warn(
            '[Auth] User created but confirmation_sent_at is null - email may not have been sent'
          )
        }

        return { needsConfirmation, userId: data.user?.id, createdAt: data.user?.created_at }
      } catch (err) {
        console.error('[Auth] signUp exception:', err)
        const errorMessage =
          err instanceof Error ? err.message : 'An unexpected error occurred during sign up'
        return { error: errorMessage }
      }
    },
    [applyDevAuthMode]
  )

  const signIn = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      if (DEV_AUTH_BYPASS) {
        applyDevAuthMode('free')
        return {}
      }

      const { error } = await supabase.auth.signInWithPassword({ email, password })
      return error ? { error: error.message } : {}
    },
    [applyDevAuthMode]
  )

  const signOut = useCallback(
    async (scope: 'local' | 'global' = 'local') => {
      if (DEV_AUTH_BYPASS) {
        applyDevAuthMode('anonymous')
        return
      }

      // Default to current-device sign-out; callers may explicitly request a global sign-out.
      await revokeCurrentDevicePushToken().catch((error) => {
        console.warn('[Auth] Failed to revoke push token before sign out:', error)
      })
      if (isNativeMobile()) {
        const { clearRevenueCatLocalState } = await import('../mobile/revenueCat')
        await clearRevenueCatLocalState().catch((error) => {
          console.warn('[Billing] RevenueCat local cleanup failed:', error)
        })
      }
      const { error } = await supabase.auth.signOut({ scope })
      if (error) throw error

      // Do not rely on the SDK's SIGNED_OUT callback timing. Native routing reads this
      // state immediately after signOut resolves and would otherwise see the stale user.
      applyAuthSession(null)
      setProfile(null)
      setLoading(false)
    },
    [applyAuthSession, applyDevAuthMode]
  )

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email)
    return error ? { error: error.message } : {}
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      loading,
      profile,
      isPremium: profile?.is_premium ?? false,
      refreshProfile,
      signUp,
      signIn,
      signOut,
      resetPassword,
    }),
    [user, session, loading, profile, refreshProfile, signUp, signIn, signOut, resetPassword]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
